import { createApp, type App, type ComponentPublicInstance } from 'vue'
import type { ToastConfig } from '@/types/dialog'
import Toast from '@/components/base/toast/Toast.vue'

// 定义 Toast 组件实例类型
type ToastInstance = ComponentPublicInstance & {
  show: (config: ToastConfig) => void
  hide: () => void
}

// Toast 实例管理
class ToastService {
  private instances: Array<{ app: App; container: HTMLElement; instance: ToastInstance }> = []

  /**
   * 显示 Toast
   */
  show(config: ToastConfig) {
    const container = document.createElement('div')
    document.body.appendChild(container)

    const app = createApp(Toast)
    const instance = app.mount(container) as ToastInstance

    // 记录实例
    this.instances.push({ app, container, instance })

    // 显示 Toast
    instance.show({
      ...config,
      onClose: () => {
        config.onClose?.()
        this.destroyInstance(app, container)
      }
    })

    return instance
  }

  /**
   * 成功提示
   */
  success(message: string, options?: Partial<ToastConfig>) {
    return this.show({
      message,
      type: 'success',
      ...options
    })
  }

  /**
   * 错误提示
   */
  error(message: string, options?: Partial<ToastConfig>) {
    return this.show({
      message,
      type: 'error',
      ...options
    })
  }

  /**
   * 警告提示
   */
  warning(message: string, options?: Partial<ToastConfig>) {
    return this.show({
      message,
      type: 'warning',
      ...options
    })
  }

  /**
   * 信息提示
   */
  info(message: string, options?: Partial<ToastConfig>) {
    return this.show({
      message,
      type: 'info',
      ...options
    })
  }

  /**
   * 加载提示
   */
  loading(message: string = 'Loading...', options?: Partial<ToastConfig>) {
    return this.show({
      message,
      type: 'loading',
      duration: 0, // 默认不自动关闭
      ...options
    })
  }

  /**
   * 销毁实例
   */
  private destroyInstance(app: App, container: HTMLElement) {
    try {
      app.unmount()
      if (container.parentNode) {
        container.parentNode.removeChild(container)
      }

      // 从实例列表中移除
      const index = this.instances.findIndex(
        (item) => item.app === app && item.container === container
      )
      if (index > -1) {
        this.instances.splice(index, 1)
      }
    } catch (error) {
      console.error('Toast destroy error:', error)
    }
  }

  /**
   * 清除所有 Toast
   */
  clear() {
    // 创建实例副本避免在遍历时修改数组
    const instancesToClear = [...this.instances]

    // 立即清空实例数组
    this.instances = []

    instancesToClear.forEach(({ app, container, instance }) => {
      try {
        // 先调用 hide 方法触发动画
        instance.hide()

        // 立即销毁 DOM，因为动画已经通过 hide() 触发
        setTimeout(() => {
          try {
            app.unmount()
            if (container.parentNode) {
              container.parentNode.removeChild(container)
            }
          } catch (error) {
            console.error('Toast destroy error:', error)
          }
        }, 300) // 等待动画完成

      } catch (error) {
        console.warn('Failed to hide toast:', error)
        // 如果 hide 失败，直接销毁
        try {
          app.unmount()
          if (container.parentNode) {
            container.parentNode.removeChild(container)
          }
        } catch (destroyError) {
          console.error('Toast destroy error:', destroyError)
        }
      }
    })
  }
}

// 创建实例
const toast = new ToastService()

export default toast
export { ToastService }
export type { ToastConfig } from '@/types/dialog'
